import React, { useState } from "react";
import axios from "axios";
import {
  PenLine,
  ListChecks,
  Trash2,
  RotateCcw,
  CheckCircle2,
  MessageCircle,
  Pencil,
  ImageIcon,
  KeyRound,
  Church,
  CalendarDays,
  HeartHandshake,
} from "lucide-react";
import { needTypeLabels } from "../../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full h-11 rounded-2xl bg-secondary/60 border-0 px-4 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";
const textareaCls =
  "w-full rounded-2xl bg-secondary/60 border-0 px-4 py-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 resize-none";
const saveBtnCls =
  "rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm font-medium hover:bg-primary/90 transition-gentle disabled:opacity-60";
const deleteBtnCls =
  "inline-flex items-center gap-1 rounded-full bg-destructive/10 text-destructive px-3 py-1.5 text-[11px] font-medium hover:bg-destructive hover:text-destructive-foreground transition-gentle shrink-0";

export const ContentEditor = ({ slug, data, setData, storedCode, toast }) => {
  const p = data.person;
  const church = p.church || {};
  const [form, setForm] = useState({
    heroTitle: p.heroTitle || "",
    verse: p.verse || "",
    verseRef: p.verseRef || "",
    heroSubtitle: p.heroSubtitle || "",
    story: p.story || "",
    currentRoom: p.currentRoom || "",
    giftCardEmail: p.giftCardEmail || "",
    surgeonName: p.surgeonName || "",
    surgeryDate: p.surgeryDate || "",
    overview: (p.overview || []).join("\n\n"),
    churchName: church.name || "",
    churchDescription: church.description || "",
    churchSiteUrl: church.siteUrl || "",
    churchStreamUrl: church.streamUrl || "",
  });
  const [passcodes, setPasscodes] = useState({ visitor: "", admin: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API}/persons/${slug}/content`, {
        adminPasscode: storedCode(),
        heroTitle: form.heroTitle,
        verse: form.verse,
        verseRef: form.verseRef,
        heroSubtitle: form.heroSubtitle,
        story: form.story,
        currentRoom: form.currentRoom,
        giftCardEmail: form.giftCardEmail,
        surgeonName: form.surgeonName,
        surgeryDate: form.surgeryDate,
        overview: form.overview.split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean),
        church: {
          name: form.churchName,
          description: form.churchDescription,
          siteUrl: form.churchSiteUrl,
          streamUrl: form.churchStreamUrl,
        },
        newVisitorPasscode: passcodes.visitor.trim() || null,
        newAdminPasscode: passcodes.admin.trim() || null,
      });
      if (passcodes.admin.trim()) {
        sessionStorage.setItem(`hart_admin_${slug}`, passcodes.admin.trim());
      }
      setData((prev) => ({
        ...prev,
        person: res.data,
        visitorPasscode: passcodes.visitor.trim() || prev.visitorPasscode,
      }));
      setPasscodes({ visitor: "", admin: "" });
      toast({ title: "Page updated", description: "Your changes are live for everyone." });
    } catch (err) {
      toast({ title: "Couldn't save", description: err.response?.data?.detail || "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("passcode", data.visitorPasscode);
      fd.append("file", file);
      const up = await axios.post(`${API}/persons/${slug}/upload-image`, fd);
      const res = await axios.post(`${API}/persons/${slug}/content`, {
        adminPasscode: storedCode(),
        photo: up.data.url,
      });
      setData((prev) => ({ ...prev, person: res.data }));
      toast({ title: "Photo updated", description: "The page photo has been changed." });
    } catch (err) {
      toast({ title: "Couldn't upload", description: err.response?.data?.detail || "Please try again." });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <form onSubmit={save} className="rounded-3xl bg-card shadow-card p-6 space-y-3">
      <div className="flex items-center gap-2 mb-1">
        <PenLine className="h-4 w-4 text-primary" />
        <h2 className="font-serif text-xl text-foreground">Edit page content</h2>
      </div>

      {/* Page photo */}
      <div className="flex items-center gap-4 rounded-2xl bg-secondary/40 p-3">
        {p.photo && (
          <img src={p.photo.startsWith("http") ? p.photo : `${process.env.REACT_APP_BACKEND_URL}${p.photo}`} alt="Page" className="h-16 w-16 rounded-2xl object-cover" />
        )}
        <div className="flex-1">
          <div className="text-sm text-foreground flex items-center gap-1.5">
            <ImageIcon className="h-3.5 w-3.5 text-primary" /> Page photo
          </div>
          <p className="text-[11px] text-muted-foreground">Shown at the top of the page and on the passcode screen.</p>
        </div>
        <label className="rounded-full bg-primary-soft text-primary px-4 py-2 text-xs font-medium cursor-pointer hover:bg-primary hover:text-primary-foreground transition-gentle">
          {uploading ? "Uploading…" : "Change photo"}
          <input type="file" accept="image/*" className="hidden" data-testid="content-photo-upload" onChange={uploadPhoto} disabled={uploading} />
        </label>
      </div>

      <div>
        <label className="text-xs text-muted-foreground">Page title</label>
        <input className={`${inputCls} mt-1`} data-testid="content-hero-title" value={form.heroTitle} onChange={set("heroTitle")} />
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <label className="text-xs text-muted-foreground">Scripture / verse</label>
          <input className={`${inputCls} mt-1`} data-testid="content-verse" value={form.verse} onChange={set("verse")} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Verse reference</label>
          <input className={`${inputCls} mt-1`} data-testid="content-verse-ref" value={form.verseRef} onChange={set("verseRef")} />
        </div>
      </div>
      <div>
        <label className="text-xs text-muted-foreground">Intro under the title</label>
        <textarea className={`${textareaCls} mt-1 min-h-[80px]`} data-testid="content-hero-subtitle" value={form.heroSubtitle} onChange={set("heroSubtitle")} />
      </div>
      <div>
        <label className="text-xs text-muted-foreground">The story (shown in "The journey" section)</label>
        <textarea className={`${textareaCls} mt-1 min-h-[110px]`} data-testid="content-story" value={form.story} onChange={set("story")} />
      </div>
      <div>
        <label className="text-xs text-muted-foreground">"What's happening" paragraphs (blank line between each)</label>
        <textarea className={`${textareaCls} mt-1 min-h-[120px]`} data-testid="content-overview" value={form.overview} onChange={set("overview")} />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Current room (optional)</label>
          <input className={`${inputCls} mt-1`} data-testid="content-room" placeholder="e.g. Recovery Room" value={form.currentRoom} onChange={set("currentRoom")} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Gift card email</label>
          <input className={`${inputCls} mt-1`} data-testid="content-gift-email" value={form.giftCardEmail} onChange={set("giftCardEmail")} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Care team / doctor (optional)</label>
          <input className={`${inputCls} mt-1`} data-testid="content-surgeon" value={form.surgeonName} onChange={set("surgeonName")} />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Key date (optional)</label>
          <input className={`${inputCls} mt-1`} data-testid="content-surgery-date" placeholder="e.g. 2026-06-15" value={form.surgeryDate} onChange={set("surgeryDate")} />
        </div>
      </div>

      {/* Church section */}
      <div className="rounded-2xl bg-secondary/40 p-4 space-y-3">
        <div className="text-sm text-foreground flex items-center gap-1.5">
          <Church className="h-3.5 w-3.5 text-primary" /> Church / faith family section
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <input className={inputCls} data-testid="content-church-name" placeholder="Church name" value={form.churchName} onChange={set("churchName")} />
          <input className={inputCls} data-testid="content-church-site" placeholder="Website URL" value={form.churchSiteUrl} onChange={set("churchSiteUrl")} />
        </div>
        <textarea className={`${textareaCls} min-h-[60px]`} data-testid="content-church-description" placeholder="Short description" value={form.churchDescription} onChange={set("churchDescription")} />
        <input className={inputCls} data-testid="content-church-stream" placeholder="Live stream / YouTube URL" value={form.churchStreamUrl} onChange={set("churchStreamUrl")} />
      </div>

      {/* Passcodes */}
      <div className="rounded-2xl bg-secondary/40 p-4 space-y-3">
        <div className="text-sm text-foreground flex items-center gap-1.5">
          <KeyRound className="h-3.5 w-3.5 text-primary" /> Change passcodes
        </div>
        <p className="text-[11px] text-muted-foreground">Leave blank to keep the current ones. Minimum 4 characters.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            className={inputCls}
            data-testid="content-visitor-passcode"
            placeholder={`Visitor passcode (now: ${data.visitorPasscode})`}
            value={passcodes.visitor}
            onChange={(e) => setPasscodes({ ...passcodes, visitor: e.target.value })}
          />
          <input
            className={inputCls}
            data-testid="content-admin-passcode"
            placeholder="New admin passcode"
            value={passcodes.admin}
            onChange={(e) => setPasscodes({ ...passcodes, admin: e.target.value })}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button type="submit" disabled={saving} data-testid="content-save" className={saveBtnCls}>
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
};

const NeedEditForm = ({ slug, need, storedCode, toast, onSaved, onCancel }) => {
  const [form, setForm] = useState({
    title: need.title,
    details: need.details || "",
    link: need.link || "",
    category: need.category || "main",
    type: need.type || "errand",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.title.trim()) {
      toast({ title: "Almost there", description: "The need must have a title." });
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/persons/${slug}/needs/${need.id}/edit`, {
        adminPasscode: storedCode(),
        ...form,
      });
      onSaved(res.data);
      toast({ title: "Need updated", description: "The change is live on the page." });
    } catch (err) {
      toast({ title: "Couldn't save", description: "Please try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full space-y-2 pt-2" data-testid={`need-edit-form-${need.id}`}>
      <input className={inputCls} data-testid="need-edit-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" />
      <textarea className={`${textareaCls} min-h-[60px]`} data-testid="need-edit-details" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="Details" />
      <input className={inputCls} data-testid="need-edit-link" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="Link (gift card / Venmo URL — leave blank for physical tasks)" />
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="h-9 rounded-full bg-secondary/60 border-0 px-3 text-xs focus-visible:outline-none"
          data-testid="need-edit-type"
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
        >
          {Object.entries(needTypeLabels).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select
          className="h-9 rounded-full bg-secondary/60 border-0 px-3 text-xs focus-visible:outline-none"
          data-testid="need-edit-category"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
        >
          <option value="main">Main board</option>
          <option value="maya">Back to School board</option>
        </select>
        <div className="flex-1" />
        <button type="button" onClick={onCancel} className="rounded-full bg-secondary text-foreground px-4 py-1.5 text-[11px] font-medium hover:bg-secondary/70 transition-gentle">
          Cancel
        </button>
        <button type="button" onClick={save} disabled={saving} data-testid="need-edit-save" className="rounded-full bg-primary text-primary-foreground px-4 py-1.5 text-[11px] font-medium hover:bg-primary/90 transition-gentle disabled:opacity-60">
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
};

export const NeedsManager = ({ slug, data, setData, storedCode, toast }) => {
  const [busyId, setBusyId] = useState("");
  const [editingId, setEditingId] = useState("");

  const manage = async (id, action) => {
    if (action === "delete" && !window.confirm("Delete this need from the page?")) return;
    setBusyId(id);
    try {
      const res = await axios.post(`${API}/persons/${slug}/needs/${id}/manage`, {
        adminPasscode: storedCode(),
        action,
      });
      setData((prev) => ({
        ...prev,
        needs:
          action === "delete"
            ? prev.needs.filter((n) => n.id !== id)
            : prev.needs.map((n) => (n.id === id ? res.data : n)),
      }));
      toast({
        title: action === "delete" ? "Need removed" : action === "reset" ? "Need reset" : "Marked completed",
        description: "The page reflects it right away.",
      });
    } catch (err) {
      toast({ title: "Couldn't do that", description: "Please try again." });
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="rounded-3xl bg-card shadow-card p-6">
      <div className="flex items-center gap-2 mb-1">
        <ListChecks className="h-4 w-4 text-primary" />
        <h2 className="font-serif text-xl text-foreground">Manage needs & gift cards</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Edit any item, reset a gift so it can be given again, mark things done, or remove them.
        Gifts with a link reload automatically — they can always be given again.
      </p>
      <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="needs-manager-list">
        {data.needs.map((n) => (
          <div key={n.id} className="rounded-2xl bg-secondary/50 p-3 flex items-center gap-3 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <div className="text-sm text-foreground">{n.title}</div>
              <div className="text-[11px] text-muted-foreground">
                {n.category === "maya" ? "Back to School board · " : ""}
                {n.status}
                {n.claimedBy ? ` · ${n.claimedBy}` : ""}
                {n.claimCount > 0 ? ` · ${n.claimCount} sent` : ""}
              </div>
            </div>
            <div className="flex gap-1.5">
              <button
                type="button"
                title="Edit this need"
                disabled={busyId === n.id}
                data-testid={`manage-edit-${n.id}`}
                onClick={() => setEditingId(editingId === n.id ? "" : n.id)}
                className="inline-flex items-center gap-1 rounded-full bg-secondary text-foreground px-3 py-1.5 text-[11px] font-medium hover:bg-secondary/70 transition-gentle"
              >
                <Pencil className="h-3 w-3" /> Edit
              </button>
              <button
                type="button"
                title="Reset — make it claimable again"
                disabled={busyId === n.id}
                data-testid={`manage-reset-${n.id}`}
                onClick={() => manage(n.id, "reset")}
                className="inline-flex items-center gap-1 rounded-full bg-primary-soft text-primary px-3 py-1.5 text-[11px] font-medium hover:bg-primary hover:text-primary-foreground transition-gentle"
              >
                <RotateCcw className="h-3 w-3" /> Reset
              </button>
              <button
                type="button"
                title="Mark completed"
                disabled={busyId === n.id}
                data-testid={`manage-complete-${n.id}`}
                onClick={() => manage(n.id, "complete")}
                className="inline-flex items-center gap-1 rounded-full bg-secondary text-foreground px-3 py-1.5 text-[11px] font-medium hover:bg-secondary/70 transition-gentle"
              >
                <CheckCircle2 className="h-3 w-3" /> Done
              </button>
              <button
                type="button"
                title="Delete"
                disabled={busyId === n.id}
                data-testid={`manage-delete-${n.id}`}
                onClick={() => manage(n.id, "delete")}
                className={deleteBtnCls}
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
            {editingId === n.id && (
              <NeedEditForm
                slug={slug}
                need={n}
                storedCode={storedCode}
                toast={toast}
                onCancel={() => setEditingId("")}
                onSaved={(updated) => {
                  setData((prev) => ({
                    ...prev,
                    needs: prev.needs.map((x) => (x.id === updated.id ? updated : x)),
                  }));
                  setEditingId("");
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export const FeedManager = ({ slug, data, setData, storedCode, toast }) => {
  const [busyId, setBusyId] = useState("");

  const remove = async (kind, id, label) => {
    if (!window.confirm(`Delete this ${label}?`)) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/persons/${slug}/${kind}/${id}/delete`, { adminPasscode: storedCode() });
      const stateKey = { updates: "updates", messages: "messages", milestones: "milestones", photos: "photos", "prayer-notes": "prayerNotes" }[kind];
      setData((prev) => ({ ...prev, [stateKey]: prev[stateKey].filter((x) => x.id !== id) }));
      toast({ title: "Removed", description: "It's gone from the page." });
    } catch (err) {
      toast({ title: "Couldn't remove", description: "Please try again." });
    } finally {
      setBusyId("");
    }
  };

  const Row = ({ children, id, kind, label, testId }) => (
    <div className="rounded-2xl bg-secondary/50 p-3 flex items-center gap-3">
      <div className="flex-1 text-sm text-foreground truncate">{children}</div>
      <button type="button" disabled={busyId === id} data-testid={testId} onClick={() => remove(kind, id, label)} className={deleteBtnCls}>
        <Trash2 className="h-3 w-3" /> Delete
      </button>
    </div>
  );

  const Section = ({ title, icon, children }) => (
    <div className="mb-4">
      <div className="text-[11px] uppercase tracking-[0.2em] text-primary mb-2 flex items-center gap-1.5">
        {icon} {title}
      </div>
      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">{children}</div>
    </div>
  );

  const empty =
    !data.updates.length && !data.messages.length && !(data.milestones || []).length &&
    !(data.photos || []).length && !(data.prayerNotes || []).length;

  return (
    <div className="rounded-3xl bg-card shadow-card p-6">
      <div className="flex items-center gap-2 mb-1">
        <MessageCircle className="h-4 w-4 text-primary" />
        <h2 className="font-serif text-xl text-foreground">Manage everything on the page</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Remove any update, message, prayer, photo, or appointment.</p>

      {data.updates.length > 0 && (
        <Section title="Updates" icon={<MessageCircle className="h-3 w-3" />}>
          {data.updates.map((u) => (
            <Row key={u.id} id={u.id} kind="updates" label="update" testId={`delete-update-${u.id}`}>
              {u.title || u.body}
            </Row>
          ))}
        </Section>
      )}

      {data.messages.length > 0 && (
        <Section title="Messages" icon={<MessageCircle className="h-3 w-3" />}>
          {data.messages.map((m) => (
            <Row key={m.id} id={m.id} kind="messages" label="message" testId={`delete-message-${m.id}`}>
              {m.name}: {m.body}
            </Row>
          ))}
        </Section>
      )}

      {(data.prayerNotes || []).length > 0 && (
        <Section title="Prayer notes" icon={<HeartHandshake className="h-3 w-3" />}>
          {data.prayerNotes.map((pn) => (
            <Row key={pn.id} id={pn.id} kind="prayer-notes" label="prayer note" testId={`delete-prayer-${pn.id}`}>
              {pn.name}: {pn.note}
            </Row>
          ))}
        </Section>
      )}

      {(data.photos || []).length > 0 && (
        <Section title="Photo wall" icon={<ImageIcon className="h-3 w-3" />}>
          {data.photos.map((ph) => (
            <div key={ph.id} className="rounded-2xl bg-secondary/50 p-3 flex items-center gap-3">
              <img
                src={ph.url.startsWith("http") ? ph.url : `${process.env.REACT_APP_BACKEND_URL}${ph.url}`}
                alt={ph.caption || "Photo"}
                className="h-12 w-12 rounded-xl object-cover shrink-0"
              />
              <div className="flex-1 text-sm text-foreground truncate">
                {ph.caption || "No caption"} {ph.by ? `· ${ph.by}` : ""}
              </div>
              <button type="button" disabled={busyId === ph.id} data-testid={`delete-photo-${ph.id}`} onClick={() => remove("photos", ph.id, "photo")} className={deleteBtnCls}>
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            </div>
          ))}
        </Section>
      )}

      {(data.milestones || []).length > 0 && (
        <Section title="Appointments" icon={<CalendarDays className="h-3 w-3" />}>
          {data.milestones.map((ms) => (
            <Row key={ms.id} id={ms.id} kind="milestones" label="appointment" testId={`delete-milestone-${ms.id}`}>
              {ms.date} — {ms.title}
            </Row>
          ))}
        </Section>
      )}

      {empty && <p className="text-sm text-muted-foreground">Nothing to manage yet.</p>}
    </div>
  );
};
